import React from 'react';
import { NavLink, useLocation, Outlet } from 'react-router-dom';
import { Home, Briefcase, ListChecks, Users, BarChart3, User, Bot } from 'lucide-react';
import { motion } from 'framer-motion';
import UserMenu from './UserMenu';

const navItems = [
  { name: 'Dashboard', icon: Home, path: '/dashboard' },
  { name: 'Projects', icon: Briefcase, path: '/projects' },
  { name: 'Tasks', icon: ListChecks, path: '/tasks' },
  { name: 'Teams', icon: Users, path: '/teams' },
  { name: 'Reports', icon: BarChart3, path: '/reports' },
];

const Sidebar = () => {
  const location = useLocation();

  return (
    <motion.aside 
      initial={{ x: -250 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
      className="w-64 bg-gradient-to-br from-slate-900 to-slate-800 p-6 flex flex-col fixed h-full shadow-2xl"
    >
      <div className="flex items-center mb-10">
        <Bot className="h-10 w-10 text-blue-400 mr-3" />
        <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">Leading Edge AI</h1>
      </div>
      <nav className="flex-grow">
        <ul>
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <li key={item.name} className="mb-3">
                <NavLink
                  to={item.path}
                  className={
                    `flex items-center p-3 rounded-lg transition-all duration-200 ease-in-out hover:bg-slate-700/50 group ${
                      isActive ? 'bg-blue-600/30 text-blue-300 shadow-md border-l-4 border-blue-400' : 'text-slate-300 hover:text-white'
                    }`
                  }
                >
                  <item.icon className={`h-5 w-5 mr-4 transition-colors group-hover:text-blue-400 ${isActive ? 'text-blue-400' : 'text-slate-400'}`} />
                  <span className="font-medium">{item.name}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
      <div className="mt-auto">
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            `flex items-center p-3 rounded-lg group transition-all duration-200 ease-in-out hover:bg-slate-700/50 ${
              isActive ? 'bg-blue-600/30 text-blue-300 shadow-md border-l-4 border-blue-400' : 'text-slate-300 hover:text-white'
            }`
          }
        >
          {({ isActive }) => (
            <>
              <User className={`h-5 w-5 mr-4 group-hover:text-blue-400 ${isActive ? 'text-blue-400' : 'text-slate-400'}`} />
              <span className="font-medium">Profile</span>
            </>
          )}
        </NavLink>
      </div>
    </motion.aside>
  );
};

const Header = () => (
  <motion.header 
    initial={{ y: -60, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 0.5, ease: "easeInOut", delay: 0.2 }}
    className="bg-slate-800/50 backdrop-blur-md p-4 shadow-lg flex justify-between items-center sticky top-0 z-40"
  >
    <div>
      {/* Placeholder for breadcrumbs or page title */}
    </div>
    <UserMenu />
  </motion.header>
);

const Layout = () => {
  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col ml-64">
        <Header />
        <motion.main 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut", delay: 0.4 }}
          className="flex-1 p-8 overflow-y-auto"
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
};

export default Layout;